import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import {
  Bell, Check, X, Inbox, ScrollText, FileText, ShieldCheck, MessageSquare,
  LifeBuoy, Sparkles, ArrowRight, RefreshCw,
} from "lucide-react";

/**
 * NotificationsCenter , HubSpot-style dropdown surfaced from the app header.
 *
 * Behavior:
 *  - polls /api/notifications on mount + every 60s
 *  - unread count exposed via prop callback so header can show a badge
 *  - grouped by kind, filterable via tabs (All / Cases / Policies / Support)
 *  - clicking a notification navigates to its href and marks-all-read
 */

const KIND_META = {
  case_assigned:      { icon: FileText,      tint: "bg-violet-50 text-violet-700 border-violet-200",   label: "Case in your queue" },
  case_update:        { icon: MessageSquare, tint: "bg-blue-50 text-blue-700 border-blue-200",         label: "Case update" },
  findings_pending:    { icon: ShieldCheck,   tint: "bg-rose-50 text-rose-700 border-rose-200",         label: "Findings awaiting approval" },
  policy_signature_due:{ icon: ScrollText,   tint: "bg-amber-50 text-amber-700 border-amber-200",       label: "Policy signature due" },
  ticket_reply:       { icon: LifeBuoy,      tint: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Support reply" },
  workflow_step_assigned: { icon: Sparkles,  tint: "bg-slate-100 text-slate-700 border-slate-200",     label: "Workflow step assigned" },
};

const FILTERS = [
  { key: "all", label: "All", kinds: null },
  { key: "cases", label: "Cases", kinds: ["case_assigned", "case_update", "findings_pending", "workflow_step_assigned"] },
  { key: "policies", label: "Policies", kinds: ["policy_signature_due"] },
  { key: "support", label: "Support", kinds: ["ticket_reply"] },
];

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function NotificationsCenter({ onUnreadChange }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ items: [], unread_count: 0, total: 0 });
  const [filter, setFilter] = useState("all");
  const ref = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 30 } });
      setData(data);
      onUnreadChange?.(data.unread_count || 0);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-read");
      setData((d) => ({ ...d, unread_count: 0 }));
      onUnreadChange?.(0);
    } catch { /* silent */ }
  };

  const handleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next && data.unread_count > 0) {
      // mark on open (HubSpot pattern)
      markAllRead();
    }
  };

  const filtered = (data.items || []).filter((n) => {
    const f = FILTERS.find((x) => x.key === filter);
    return !f?.kinds || f.kinds.includes(n.kind);
  });

  return (
    <div ref={ref} className="relative">
      <button
        onClick={handleOpen}
        className="p-1.5 hover:bg-slate-100 rounded transition-colors relative"
        data-testid="notifications-btn"
        title="Notifications"
      >
        <Bell size={18} strokeWidth={1.75} className="text-slate-600"/>
        {data.unread_count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center bg-red-600 text-white text-[9px] font-mono font-bold rounded-full"
            data-testid="notif-badge"
          >
            {data.unread_count > 99 ? "99+" : data.unread_count}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
            className="absolute right-0 top-full mt-1 w-[420px] max-w-[92vw] bg-white border border-slate-200 shadow-lg rounded-md overflow-hidden z-50"
            data-testid="notif-panel"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50/70">
              <div className="flex items-center gap-2">
                <Bell size={14} strokeWidth={2} className="text-slate-700"/>
                <div className="text-sm font-semibold text-slate-900">Notifications</div>
                {data.total > 0 && (
                  <span className="text-[10px] font-mono text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">
                    {data.total}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={load}
                  disabled={loading}
                  className="p-1.5 hover:bg-slate-100 rounded transition-colors"
                  title="Refresh"
                  data-testid="notif-refresh"
                >
                  <RefreshCw size={12} strokeWidth={2} className={"text-slate-500 " + (loading ? "animate-spin" : "")}/>
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="p-1.5 hover:bg-slate-100 rounded transition-colors"
                  title="Close"
                >
                  <X size={12} strokeWidth={2} className="text-slate-500"/>
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-slate-200 flex gap-1 px-2 bg-white">
              {FILTERS.map((f) => {
                const count = f.kinds ? (data.items || []).filter((n) => f.kinds.includes(n.kind)).length : (data.items || []).length;
                const active = filter === f.key;
                return (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    className={
                      "px-3 py-2 text-xs font-medium border-b-2 whitespace-nowrap transition-colors flex items-center gap-1.5 " +
                      (active ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900")
                    }
                    data-testid={`notif-tab-${f.key}`}
                  >
                    {f.label}
                    {count > 0 && (
                      <span className={"text-[10px] font-mono " + (active ? "text-slate-500" : "text-slate-400")}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
              <div className="flex-1"/>
              {data.total > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-[11px] text-blue-700 hover:underline my-2 inline-flex items-center gap-1 mr-1"
                  data-testid="notif-mark-all-read"
                >
                  <Check size={11}/> Mark all read
                </button>
              )}
            </div>

            {/* List */}
            <div className="max-h-[420px] overflow-y-auto">
              {loading && filtered.length === 0 ? (
                <div className="text-center text-xs text-slate-500 py-8 font-mono">Loading…</div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-10 px-6">
                  <Inbox size={22} strokeWidth={1.5} className="mx-auto text-slate-400 mb-2"/>
                  <div className="text-sm font-medium text-slate-900">You&apos;re all caught up</div>
                  <div className="text-xs text-slate-500 mt-1">
                    {filter === "all" ? "No notifications right now." : `No ${filter} notifications.`}
                  </div>
                </div>
              ) : (
                <div>
                  {filtered.map((n) => {
                    const meta = KIND_META[n.kind] || { icon: Bell, tint: "bg-slate-50 text-slate-700 border-slate-200", label: "Update" };
                    const Icon = meta.icon;
                    return (
                      <Link
                        key={n.id}
                        to={n.href || "#"}
                        onClick={() => setOpen(false)}
                        className="flex items-start gap-3 px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors last:border-b-0"
                        data-testid={`notif-item-${n.kind}`}
                      >
                        <div className={"w-8 h-8 border flex items-center justify-center shrink-0 " + meta.tint}>
                          <Icon size={14} strokeWidth={1.75}/>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 truncate">
                              {meta.label}
                            </div>
                            {n.is_urgent && (
                              <span className="text-[9px] font-mono uppercase tracking-widest text-rose-700 bg-rose-50 border border-rose-200 rounded px-1">
                                URGENT
                              </span>
                            )}
                          </div>
                          <div className="text-sm font-medium text-slate-900 truncate">{n.title}</div>
                          {n.body && (
                            <div className="text-xs text-slate-600 line-clamp-2 mt-0.5 leading-relaxed">{n.body}</div>
                          )}
                          <div className="text-[10px] font-mono text-slate-400 mt-1">{timeAgo(n.created_at)}</div>
                        </div>
                        <ArrowRight size={12} strokeWidth={2} className="text-slate-400 mt-1 shrink-0"/>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-slate-200 px-4 py-2.5 flex items-center justify-between bg-slate-50/70">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
                Live · updates every minute
              </div>
              <Link
                to="/settings/notifications"
                onClick={() => setOpen(false)}
                className="text-[11px] text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
                data-testid="notif-preferences-link"
              >
                Preferences <ArrowRight size={11}/>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
